import {
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  DocumentMagnifyingGlassIcon,
  GlobeAltIcon,
  HomeIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import type { NavGroup } from '@csa/ui';
import type { PermissionKey } from '@csa/shared';

/**
 * The navigation map, with the permission each destination needs.
 *
 * Items the user cannot use are hidden rather than disabled. A greyed-out
 * "Approvals" link tells someone the feature exists and that they have been
 * refused it, which generates support tickets and tells an attacker what to
 * aim at. The server enforces the same permissions independently -- this is
 * presentation, not a control.
 */
export interface NavDefinition {
  readonly key: string;
  readonly label: string;
  readonly href: string;
  readonly icon: NavGroup['items'][number]['icon'];
  /** Any one of these is sufficient. Empty means always visible. */
  readonly anyOf: readonly PermissionKey[];
}

export interface NavGroupDefinition {
  readonly label: string;
  readonly items: readonly NavDefinition[];
}

export const NAVIGATION: readonly NavGroupDefinition[] = [
  {
    label: 'Work',
    items: [
      { key: 'home', label: 'Overview', href: '/', icon: HomeIcon, anyOf: [] },
      {
        key: 'sql',
        label: 'SQL console',
        href: '/sql',
        icon: CommandLineIcon,
        anyOf: ['query.execute.read', 'query.execute.write'],
      },
      {
        key: 'api',
        label: 'API console',
        href: '/api-console',
        icon: GlobeAltIcon,
        anyOf: ['api.request.send', 'api.request.mutate'],
      },
      {
        // Own jobs need no permission of their own -- if you could submit the
        // work you can see the result. The keys here are simply "can this
        // person cause a job to exist", plus the administrative all-jobs view.
        key: 'jobs',
        label: 'Jobs & downloads',
        href: '/jobs',
        icon: ArrowDownTrayIcon,
        anyOf: [
          'query.execute.read',
          'query.execute.write',
          'query.execute.proc',
          'api.request.send',
          'job.view.all',
        ],
      },
    ],
  },
  {
    label: 'Governance',
    items: [
      {
        key: 'approvals',
        label: 'Approvals',
        href: '/approvals',
        icon: CheckBadgeIcon,
        anyOf: ['approval.grant'],
      },
      {
        key: 'audit',
        label: 'Audit log',
        href: '/audit',
        icon: DocumentMagnifyingGlassIcon,
        anyOf: ['audit.read'],
      },
      {
        // Under Governance rather than Administration on purpose. The
        // reviewer is usually not the administrator -- that separation is
        // the point of an access review -- so the link lives beside the
        // audit log the same people read.
        key: 'access-review',
        label: 'Access review',
        href: '/access-review',
        icon: ClipboardDocumentCheckIcon,
        anyOf: ['audit.read'],
      },
      {
        key: 'evidence',
        label: 'Evidence packs',
        href: '/evidence',
        icon: ClipboardDocumentListIcon,
        anyOf: ['audit.export'],
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        key: 'users',
        label: 'Users',
        href: '/admin/users',
        icon: UsersIcon,
        anyOf: ['user.manage'],
      },
      {
        key: 'roles',
        label: 'Roles',
        href: '/admin/roles',
        icon: ShieldCheckIcon,
        anyOf: ['role.manage'],
      },
      {
        key: 'targets',
        label: 'Targets & secrets',
        href: '/admin/targets',
        icon: ServerStackIcon,
        anyOf: ['target.manage', 'secret.bind'],
      },
      {
        key: 'policy',
        label: 'Policy & settings',
        href: '/admin/settings',
        icon: Cog6ToothIcon,
        anyOf: ['approval.rule.manage'],
      },
    ],
  },
  {
    /**
     * Its own group, at the bottom, with no permission. Everyone who can
     * sign in belongs here -- including a user holding no roles at all, who
     * would otherwise see a single "Overview" link and no way to fix the
     * fact that every date on screen is in the wrong zone.
     */
    label: 'You',
    items: [
      {
        key: 'settings',
        label: 'Your settings',
        href: '/settings',
        icon: UserCircleIcon,
        anyOf: [],
      },
    ],
  },
];

/**
 * Filters the map to what this user holds, dropping groups that end up empty
 * so an auditor-only account does not see three section headings with nothing
 * under them.
 */
export function visibleNavigation(
  held: ReadonlySet<string>,
  badges: Readonly<Record<string, number>> = {},
): NavGroup[] {
  return NAVIGATION.map((group) => ({
    label: group.label,
    items: group.items
      .filter((item) => item.anyOf.length === 0 || item.anyOf.some((p) => held.has(p)))
      .map((item) => ({
        key: item.key,
        label: item.label,
        href: item.href,
        icon: item.icon,
        ...(badges[item.key] ? { badge: badges[item.key] } : {}),
      })),
  })).filter((group) => group.items.length > 0);
}
